import { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { Copy, Loader2, MoreHorizontal, Search, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import {
  useGfsOfferDeleteMutation,
  useGfsOfferGetQuery,
  useGfsOfferLoadCalculationMutation,
} from "@/services/backend/gfs/offer/api";
import {
  useGfsCalculationGetQuery,
  useGfsCalculationListQuery,
} from "@/services/backend/gfs/calculation/api";
import { GfsCalculationListItem } from "@/services/backend/gfs/calculation/types";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  CollapsibleDocumentPageNavigation,
  DocumentPageNavigationOutlet,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import {
  GfsOfferEditorContextProvider,
  useGfsOfferEditorContext,
} from "@/services/backend/gfs/offer/components/offer-editor-context";
import { GfsOfferPdf } from "@/services/backend/gfs/offer/components/offer-pdf";
import { SetGfsOfferImmutableButton } from "@/services/backend/gfs/offer/components/set-offer-immutable-button";
import { GfsSignatureButton } from "@/services/backend/gfs/offer/components/signature-button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { formatDate } from "@/shared/lib/utilities/date";
import { toast } from "sonner";

const GFS_OFFER_NAV_LINKS = {
  overview: "overview",
  calculation: "calculation",
  object: "object",
  contact: "contact",
  coverletter: "coverletter",
  offerText: "offertext",
} as const;

export function GfsOfferEditor() {
  const { offerId = "" } = useParams();
  const { data, error } = useGfsOfferGetQuery(
    { id: offerId },
    { skip: !offerId },
  );

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (!data) return null;

  return (
    <PersistenceContextProvider>
      <GfsOfferEditorContextProvider offer={data} key={data.id}>
        <OfferPage />
      </GfsOfferEditorContextProvider>
    </PersistenceContextProvider>
  );
}

function OfferPage() {
  const { offer, replaceOffer } = useGfsOfferEditorContext();
  const isCompactHeader = useCompactHeader();

  return (
    <DocumentPage>
      <DocumentPageHeader className="block">
        {isCompactHeader ? (
          <div className="grid gap-2">
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2">
              <div className="min-w-0">
                <OfferNavigation />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="flex flex-col gap-2 p-1">
                    <DeleteOfferDialog
                      offerId={offer.id}
                      disabled={offer.immutable}
                      triggerLabel={t("Angebot löschen")}
                    />
                    <SetGfsOfferImmutableButton
                      offerId={offer.id}
                      immutable={offer.immutable}
                      triggerLabel={t("Angebot festschreiben")}
                      onUpdated={replaceOffer}
                    />
                    <GfsSignatureButton
                      offer={offer}
                      name={offer.content.representative.name}
                      disabled={offer.immutable}
                      triggerLabel={t("Unterschreiben")}
                      onUpdated={replaceOffer}
                    />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <GfsOfferPdf offer={offer} onUpdated={replaceOffer} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-sm">{t("Kalkulationsgrundlage")}:</span>
                <SelectCalculationButton
                  disabled={offer.immutable}
                  selectedCalculationId={
                    offer.sourceCalculationId ||
                    offer.calculation.sourceId ||
                    ""
                  }
                />
              </div>
              <PersistenceStateUsingContext className="shrink-0" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <OfferNavigation />
            </div>
            <DocumentPageActions>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">{t("Kalkulationsgrundlage")}:</span>
                <SelectCalculationButton
                  disabled={offer.immutable}
                  selectedCalculationId={
                    offer.sourceCalculationId ||
                    offer.calculation.sourceId ||
                    ""
                  }
                />
              </div>
              <SetGfsOfferImmutableButton
                offerId={offer.id}
                immutable={offer.immutable}
                onUpdated={replaceOffer}
              />
              <GfsSignatureButton
                offer={offer}
                name={offer.content.representative.name}
                disabled={offer.immutable}
                onUpdated={replaceOffer}
              />
              <GfsOfferPdf offer={offer} onUpdated={replaceOffer} />
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteOfferDialog
                offerId={offer.id}
                disabled={offer.immutable}
              />
            </DocumentPageActions>
          </div>
        )}
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}

function useCompactHeader(): boolean {
  const [isCompact, setIsCompact] = useState(
    () => window.matchMedia("(max-width: 1200px)").matches,
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1200px)");
    const onChange = (event: MediaQueryListEvent) => {
      setIsCompact(event.matches);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return isCompact;
}

function OfferNavigation() {
  const { pathname } = useLocation();
  const links = [
    { to: GFS_OFFER_NAV_LINKS.overview, label: t("Übersicht") },
    { to: GFS_OFFER_NAV_LINKS.calculation, label: t("Kalkulation") },
    { to: GFS_OFFER_NAV_LINKS.object, label: t("Objekt") },
    { to: GFS_OFFER_NAV_LINKS.contact, label: t("Kontakt") },
    { to: GFS_OFFER_NAV_LINKS.coverletter, label: t("Anschreiben") },
    { to: GFS_OFFER_NAV_LINKS.offerText, label: t("Angebotstexte") },
  ];
  const activeLink = links.find((link) => pathname.endsWith(`/${link.to}`));

  return (
    <CollapsibleDocumentPageNavigation
      label={t("Navigation")}
      currentLabel={activeLink?.label}
      maxVisibleTabs={5}
      priorityTabs={[
        GFS_OFFER_NAV_LINKS.overview,
        GFS_OFFER_NAV_LINKS.calculation,
        GFS_OFFER_NAV_LINKS.object,
        GFS_OFFER_NAV_LINKS.contact,
      ]}
      links={links.map((link) => ({
        to: link.to,
        label: link.label,
      }))}
    />
  );
}

function SelectCalculationButton({
  disabled,
  selectedCalculationId,
}: {
  disabled?: boolean;
  selectedCalculationId: string;
}) {
  const [open, setOpen] = useState(false);
  const { processId = "" } = useParams();
  const { data: selectedCalculation } = useGfsCalculationGetQuery(
    { id: selectedCalculationId },
    { skip: !selectedCalculationId },
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2">
        {selectedCalculationId ? (
          <NavLink
            className="text-primary text-sm underline underline-offset-4"
            to={`/processes/GFS/${processId}/calculations/${selectedCalculationId}`}
          >
            <DocumentNumberById
              documentId={selectedCalculationId}
              skip={!selectedCalculation?.immutable}
            />
          </NavLink>
        ) : (
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={disabled}>
              {t("-- nicht gewählt --")}
            </Button>
          </DialogTrigger>
        )}
        {selectedCalculationId && (
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={disabled}>
              {t("Ändern")}
            </Button>
          </DialogTrigger>
        )}
      </div>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>
            {t("Daten aus bestehender Kalkulation laden")}
          </DialogTitle>
          <DialogDescription>
            {t("Kopiert die Daten der gewählten Kalkulation in das Angebot.")}
          </DialogDescription>
        </DialogHeader>
        <CalculationSelectionTable onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

function CalculationSelectionTable({ onClose }: { onClose: () => void }) {
  const { offer, replaceOffer } = useGfsOfferEditorContext();
  const { data, isLoading, error } = useGfsCalculationListQuery({
    company: { active: true, values: [offer.companyId] },
    customer: { active: true, values: [offer.customerId] },
  });

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead>{t("Aktualisiert am")}</TableHead>
            <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
            <TableHead className="text-right">{t("Summe")}</TableHead>
            <TableHead>{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead>{t("Aktualisiert am")}</TableHead>
          <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(data?.items ?? []).map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>
              <DocumentNumberById
                documentId={calculation.id}
                skip={!calculation.immutable}
              />
            </TableCell>
            <TableCell>{formatDate(calculation.createdAt)}</TableCell>
            <TableCell>{formatDate(calculation.updatedAt)}</TableCell>
            <TableCell>
              {calculation.assessmentSubject || t("Nicht gewählt")}
            </TableCell>
            <TableCell className="text-right">
              {formatCurrency(calculation.pricingTotal)}
            </TableCell>
            <TableCell>
              <div className="flex gap-2">
                <NavLink to={`../../calculations/${calculation.id}`}>
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
                <LoadCalculationButton
                  calculation={calculation}
                  onClose={onClose}
                  onLoaded={replaceOffer}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
        {(data?.items ?? []).length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center">
              {t("Keine Kalkulationen gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function LoadCalculationButton({
  calculation,
  onClose,
  onLoaded,
}: {
  calculation: GfsCalculationListItem;
  onClose: () => void;
  onLoaded: ReturnType<typeof useGfsOfferEditorContext>["replaceOffer"];
}) {
  const { offer } = useGfsOfferEditorContext();
  const [loadCalculation, { isLoading, error }] =
    useGfsOfferLoadCalculationMutation();

  const onClick = () => {
    if (isLoading) return;
    loadCalculation({ id: offer.id, calculationId: calculation.id })
      .unwrap()
      .then((loadedOffer) => {
        onLoaded(loadedOffer);
        onClose();
        toast.success(t("Kalkulation übernommen"), {
          description: t(
            "Die Kalkulationsdaten wurden in das Angebot kopiert.",
          ),
        });
      })
      .catch(() => {
        toast.error(t("Fehler"), {
          description: t(
            "Die Kalkulationsdaten konnten nicht übernommen werden.",
          ),
        });
      });
  };

  return (
    <div className="flex items-center gap-2">
      {error && <RTKQueryErrorAlert error={error} />}
      <Button size="sm" disabled={isLoading} onClick={onClick}>
        {isLoading ? (
          <>
            <Loader2 className="mr-3 h-4 w-4 animate-spin" />
            {t("Daten werden übernommen")}
          </>
        ) : (
          <>
            <Copy className="mr-3 h-4 w-4" />
            {t("Daten Übernehmen")}
          </>
        )}
      </Button>
    </div>
  );
}

function DeleteOfferDialog({
  disabled,
  offerId,
  triggerLabel,
}: {
  disabled?: boolean;
  offerId: string;
  triggerLabel?: string;
}) {
  const navigate = useNavigate();
  const [deleteOffer, { isLoading, error }] = useGfsOfferDeleteMutation();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          size={triggerLabel ? "sm" : "icon"}
          variant={triggerLabel ? "outline" : "outline"}
          disabled={disabled}
          className={triggerLabel ? "w-full justify-start gap-2" : undefined}
        >
          <Trash2 className="h-4 w-4" />
          {triggerLabel && <span>{triggerLabel}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Angebot löschen")}</DialogTitle>
          <DialogDescription>
            {t("Das Angebot wird dauerhaft gelöscht.")}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={disabled || isLoading}
            onClick={() =>
              deleteOffer({ id: offerId })
                .unwrap()
                .then(() => navigate("../.."))
            }
          >
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(value ?? 0);
}

export function GfsOfferEditorOutlet() {
  return <Outlet />;
}
